import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Copy, Eye, Pencil, Trash2, FileText, Loader2, Upload, Send, ScanLine } from 'lucide-react';
import { api, getApiErrorMessage } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { ExtractionListRow } from '../../lib/form-extraction';
import { updateQueryDataMapped } from '../../lib/queryClient';
import { useAuth } from '../../contexts/AuthContext';
import {
  canManageFormLibrary,
  canPublishFormTemplate,
  canUploadSourceForm,
} from '../../lib/roles';
import {
  FormLibraryTemplate,
  TEMPLATE_SOURCE_LABELS,
  TEMPLATE_SOURCE_HINTS,
  TEMPLATE_SOURCE_STYLES,
  groupByCategory,
  hasNoFields,
  isContentEditable,
  isDraft,
  NO_FIELDS_REASON,
  withoutApprovalStamps,
} from '../../lib/form-library';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Tooltip } from '../../components/ui/tooltip';

const LIBRARY_QUERY_KEY = 'forms-library';

/**
 * The tenant's form library — ONE page, grouped by the categories the data
 * actually has.
 *
 * WHAT REPLACED WHAT
 * ------------------
 * This screen used to filter `GET /safety/templates` through a hardcoded
 * CATEGORIES pill list. That list offered "Inspection" and "Other", which match
 * no category on any live tenant, and had no pill at all for `general` (10 of
 * Smith Casing's 16 templates) or `incident`. Categories now come from the
 * response, so a category cannot be listed and resolve empty, and cannot exist
 * and be unreachable.
 *
 * WHY DRAFTS ARE HERE
 * -------------------
 * `cloneTemplate` hardcodes `status = 'draft'` and extraction approval defaults
 * to `publish = false`, while the strict library read filters `status =
 * 'active'`. Every route into a tenant's own library therefore lands on a
 * status that nothing moved off, and the result was invisible on the page that
 * was supposed to manage it — six tenant clones sat in draft for months, two on
 * live customers. Managers read with `include_drafts=1` and get a Publish
 * action; that is the way out.
 */
export default function SafetyFormTemplatesScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isLoading: authLoading } = useAuth();

  const role = user?.role;
  const canManage = canManageFormLibrary(role);
  const canPublish = canPublishFormTemplate(role);
  const canUpload = canUploadSourceForm(role);

  const [enabledEdits, setEnabledEdits] = useState<Record<string, boolean>>({});
  const [defaultEdits, setDefaultEdits] = useState<Record<string, string | null>>({});
  const [deleteTarget, setDeleteTarget] = useState<FormLibraryTemplate | null>(null);

  const queryKey = [LIBRARY_QUERY_KEY, canManage] as const;

  const { data: templates = [], isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: async (): Promise<FormLibraryTemplate[]> => {
      if (canManage) {
        try {
          const res = await api.forms.templates({ includeDrafts: true });
          return withoutApprovalStamps(unwrapArray<FormLibraryTemplate>(res.data));
        } catch (e: any) {
          // A 403 here means the server disagrees with our role check — the
          // page still has a strict read it is allowed to make, so fall back to
          // it rather than hiding the library. Anything else is a real failure
          // and must surface.
          if (e?.response?.status !== 403) throw e;
        }
      }
      const res = await api.forms.templates();
      return withoutApprovalStamps(unwrapArray<FormLibraryTemplate>(res.data));
    },
    // Wait for the role. Firing while `user` is still null reads as "not a
    // manager", so a manager would fetch the strict list, then refetch with
    // drafts the moment auth resolved — and for the width of that first render
    // the page would be quietly missing every unpublished form.
    enabled: !authLoading,
  });

  /**
   * Extractions waiting on a human.
   *
   * WHY THIS IS ON THIS PAGE
   * ------------------------
   * Nothing else in the app knew a `needs_review` run existed. The upload
   * screen navigates to the review screen once, and after that the run was
   * reachable only by pasting its URL — so a tenant who uploaded a form,
   * paid for the extraction and then closed the tab had no way back to it.
   * That is the "a route with no nav entry is invisible" rule, with a form
   * the customer paid for on the end of it.
   *
   * Gated on `canUploadSourceForm`: the LIST is readable by any authenticated
   * user, but PATCH/approve/reject are not, so showing this to a field worker
   * offers a door that answers 403.
   */
  const { data: pendingRuns = [] } = useQuery({
    queryKey: ['form-extractions-pending'],
    queryFn: async () => {
      const res = await api.forms.extractions({ status: 'needs_review' });
      return unwrapArray<ExtractionListRow>(res.data);
    },
    enabled: !authLoading && canUpload,
  });

  const groups = useMemo(() => groupByCategory(templates), [templates]);

  /** The library's answer, before any unsaved edit on this screen. */
  const serverDefaultFor = (category: string): string | null =>
    templates.find((t) => t.category === category && t.isDefault)?.id ?? null;

  const effectiveEnabled = (t: FormLibraryTemplate): boolean => enabledEdits[t.id] ?? t.isEnabled;

  const effectiveDefault = (category: string): string | null =>
    category in defaultEdits ? defaultEdits[category] : serverDefaultFor(category);

  const isCategoryDirty = (category: string, rows: FormLibraryTemplate[]): boolean =>
    rows.some((t) => effectiveEnabled(t) !== t.isEnabled) ||
    effectiveDefault(category) !== serverDefaultFor(category);

  const dirtyCategories = groups
    .filter((g) => isCategoryDirty(g.category, g.templates))
    .map((g) => g.category);

  const handleToggleEnabled = (t: FormLibraryTemplate, next: boolean) => {
    // Turning a zero-field form ON is blocked; turning one OFF never is. The
    // switch is only rendered disabled in the first direction for the same
    // reason — a template that is already enabled needs a way out, and locking
    // the control would take the remedy away along with the mistake.
    if (next && hasNoFields(t)) {
      toast.error(NO_FIELDS_REASON);
      return;
    }

    setEnabledEdits((prev) => ({ ...prev, [t.id]: next }));

    // Disabling the category's default would make the save 422 — "the default
    // template must be one of the enabled templates". Move the state to
    // something saveable here rather than letting the user discover it from a
    // rejected request.
    if (!next && effectiveDefault(t.category) === t.id) {
      setDefaultEdits((prev) => ({ ...prev, [t.category]: null }));
      toast.info(`"${t.name}" was the default — that category now has none.`);
    }
  };

  const handleSetDefault = (t: FormLibraryTemplate) => {
    // The default is what a crew gets handed when they pick the category, so a
    // form with nothing on it is the one row that must never hold the slot.
    if (hasNoFields(t)) {
      toast.error(NO_FIELDS_REASON);
      return;
    }
    if (!effectiveEnabled(t)) {
      toast.error('Enable this form before making it the default.');
      return;
    }
    setDefaultEdits((prev) => ({ ...prev, [t.category]: t.id }));
  };

  /**
   * One PUT per dirty category, each carrying that category's WHOLE end state.
   *
   * A partial payload is not a partial update here: the handler clears the
   * category's existing default before applying what it was sent, so omitting
   * `default_template_id` deletes the default rather than leaving it alone.
   */
  const saveMutation = useMutation({
    mutationFn: async () => {
      const saved: Array<{ category: string; resolved: FormLibraryTemplate[] }> = [];

      for (const category of dirtyCategories) {
        const group = groups.find((g) => g.category === category);
        if (!group) continue;

        // Drafts are excluded deliberately. They are not in the strict library,
        // so an is_enabled preference on one has no effect until it is
        // published — and the PUT response would not contain it either.
        const rows = group.templates.filter((t) => !isDraft(t));

        const res = await api.forms.saveLibrary({
          category,
          entries: rows.map((t) => ({
            template_id: t.id,
            is_enabled: effectiveEnabled(t),
            sort_order: t.sortOrder,
          })),
          default_template_id: effectiveDefault(category),
        });

        saved.push({ category, resolved: unwrapArray<FormLibraryTemplate>(res.data) });
      }

      return saved;
    },
    onSuccess: (saved) => {
      // The PUT returns the re-resolved library, so there is nothing to refetch.
      //
      // It is resolved WITHOUT include_drafts, though: the response holds only
      // the category's active rows. Merging by id keeps this page's draft rows
      // instead of reading their absence as a deletion.
      updateQueryDataMapped<FormLibraryTemplate[]>(queryClient, queryKey, (old = []) => {
        let next = old;
        for (const { category, resolved } of saved) {
          const byId = new Map(resolved.map((r) => [r.id, r]));
          next = next.map((t) => (t.category === category ? byId.get(t.id) ?? t : t));
          const known = new Set(next.map((t) => t.id));
          const added = resolved.filter((r) => !known.has(r.id));
          if (added.length > 0) next = [...next, ...added];
        }
        return next;
      });

      setEnabledEdits({});
      setDefaultEdits({});
      toast.success(
        saved.length === 1 ? 'Library updated' : `Library updated across ${saved.length} categories`,
      );
    },
    onError: (e: any) => toast.error(getApiErrorMessage(e, 'Could not save the library')),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => api.forms.publishTemplate(id),
    onSuccess: () => {
      // Unlike the library save, publishing changes WHICH rows the library
      // contains and how they resolve, so this one does need a refetch.
      queryClient.invalidateQueries({ queryKey: [LIBRARY_QUERY_KEY] });
      toast.success('Published — this form is now live for your crews');
    },
    onError: (e: any) => toast.error(getApiErrorMessage(e, 'Could not publish this template')),
  });

  const cloneMutation = useMutation({
    mutationFn: (id: string) => api.safety.cloneTemplate(id),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: [LIBRARY_QUERY_KEY] });
      const cloned = res.data?.data ?? res.data;
      if (cloned?.id) {
        toast.success('Forked as a draft — opening the editor');
        navigate(`/settings/safety/forms/${cloned.id}/edit`);
      } else {
        toast.success('Forked as a draft');
      }
    },
    onError: (e: any) => toast.error(getApiErrorMessage(e, 'Could not fork this template')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.safety.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIBRARY_QUERY_KEY] });
      setDeleteTarget(null);
      toast.success('Template deleted');
    },
    onError: (e: any) => toast.error(getApiErrorMessage(e, 'Could not delete this template')),
  });

  const dirty = dirtyCategories.length > 0;

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)]">
      {/* The Forms tab of the Safety workspace (settings/SafetyWorkspace.tsx),
          which owns the page title. What is left of the old title band is its
          action row, right-aligned as the actions were. */}
      <div className="px-6 pt-4">
        <div className="flex items-center justify-end gap-3">
          <div className="flex items-center gap-3 shrink-0">
            {canUpload && (
              <button
                onClick={() => navigate('/settings/safety/forms/upload')}
                className="flex items-center gap-2 bg-fieldiq-card border border-fieldiq-border text-white font-semibold px-3 sm:px-4 py-2 rounded-lg hover:border-fieldiq-accent transition-colors"
              >
                <Upload size={18} />
                <span className="hidden sm:inline">Replicate a Form</span>
              </button>
            )}
            <button
              onClick={() => navigate('/settings/safety/forms/new')}
              className="flex items-center gap-2 bg-fieldiq-accent text-white font-semibold px-3 sm:px-4 py-2 rounded-lg hover:bg-fieldiq-accent/90 transition-colors"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Create New Form</span>
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-6">
        {/* Waiting on review. Hidden entirely when there is nothing pending —
            an empty "0 waiting" panel is furniture. */}
        {pendingRuns.length > 0 && (
          <section className="rounded-lg border border-fieldiq-accent/40 bg-fieldiq-accent/10 px-4 py-3">
            <div className="flex items-start gap-2">
              <ScanLine size={18} className="text-fieldiq-accent mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-fieldiq-accent">
                  {pendingRuns.length === 1
                    ? 'An uploaded form is waiting for review'
                    : `${pendingRuns.length} uploaded forms are waiting for review`}
                </p>
                <p className="text-xs text-fieldiq-text mt-0.5">
                  FieldIQ has read {pendingRuns.length === 1 ? 'it' : 'them'}. Nothing reaches your
                  crews until someone checks the result and approves it.
                </p>

                <ul className="mt-2 divide-y divide-fieldiq-accent/20">
                  {pendingRuns.map((r) => (
                    <li
                      key={r.id}
                      className="flex flex-col gap-1.5 py-2 sm:flex-row sm:items-center sm:gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        {/* The model's proposed name, or the filename. Absent
                            renders as an em dash, never as an empty row. */}
                        <p className="text-sm text-white break-words">
                          {r.proposed_name || r.original_filename || '—'}
                        </p>
                        <p className="text-xs text-fieldiq-muted break-words">
                          {r.original_filename || '—'} ·{' '}
                          {Number.isFinite(r.page_count as number) ? `${r.page_count} pages` : '—'}
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          navigate(`/settings/safety/forms/extractions/${r.id}`)
                        }
                        className="px-3 py-1.5 bg-fieldiq-accent text-white rounded-lg text-xs font-semibold hover:bg-fieldiq-accent/90 transition-colors shrink-0 self-start sm:self-auto"
                      >
                        Review
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
          </div>
        )}

        {isError && !isLoading && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {getApiErrorMessage(error, 'Could not load the form library.')}
          </div>
        )}

        {!isLoading && !isError && templates.length === 0 && (
          <div className="text-center py-12">
            <FileText size={48} className="mx-auto text-fieldiq-muted mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No forms in your library yet</h3>
            <p className="text-fieldiq-text mb-6">
              Create a form from scratch, or upload one you already use and let FieldIQ rebuild it.
            </p>
            <button
              onClick={() => navigate('/settings/safety/forms/new')}
              className="bg-fieldiq-accent text-white font-semibold px-6 py-2.5 rounded-lg hover:bg-fieldiq-accent/90 transition-colors"
            >
              Create your first form
            </button>
          </div>
        )}

        {groups.map((group) => (
          <section key={group.category}>
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <h2 className="text-sm font-bold uppercase tracking-widest text-fieldiq-muted">
                {group.label}
              </h2>
              <span className="text-xs text-fieldiq-muted shrink-0">
                {group.templates.length} {group.templates.length === 1 ? 'form' : 'forms'}
              </span>
            </div>

            <div className="rounded-lg border border-fieldiq-border divide-y divide-fieldiq-border overflow-hidden">
              {group.templates.map((t) => {
                const draft = isDraft(t);
                const enabled = effectiveEnabled(t);
                const isDefault = effectiveDefault(t.category) === t.id;
                const editable = isContentEditable(t);
                const empty = hasNoFields(t);

                return (
                  <div
                    key={t.id}
                    className={`flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 ${
                      draft ? 'bg-yellow-500/[0.06] border-l-2 border-l-yellow-500/60' : ''
                    } ${empty && !draft ? 'bg-white/[0.02] border-l-2 border-l-fieldiq-border' : ''}`}
                  >
                    {/* Name + description */}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium break-words ${
                          empty ? 'text-fieldiq-muted' : 'text-white'
                        }`}
                      >
                        {t.name}
                      </p>
                      {t.description && (
                        <p className="text-xs text-fieldiq-muted break-words mt-0.5">
                          {t.description}
                        </p>
                      )}
                      {/* The reason, spelled out on the row. A greyed control
                          with no stated cause gets reported as a broken page. */}
                      {empty && (
                        <p className="text-xs text-amber-300/80 break-words mt-1">
                          {NO_FIELDS_REASON}{' '}
                          {editable
                            ? 'Add fields in the editor before enabling it.'
                            : 'Fork it and add the fields, or ask FieldIQ to build it out.'}
                        </p>
                      )}
                    </div>

                    {/* Badges — wrap rather than clip, at every width */}
                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                      <span
                        title={TEMPLATE_SOURCE_HINTS[t.source]}
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${TEMPLATE_SOURCE_STYLES[t.source]}`}
                      >
                        {TEMPLATE_SOURCE_LABELS[t.source]}
                      </span>
                      {draft && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-yellow-500/20 text-yellow-300">
                          Unpublished
                        </span>
                      )}
                      {empty && (
                        <span
                          title={NO_FIELDS_REASON}
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-fieldiq-border text-fieldiq-muted"
                        >
                          Unavailable
                        </span>
                      )}
                      <span className="text-xs text-fieldiq-muted">v{t.version}</span>
                      {/* Absent is not zero: a missing count reads as an em
                          dash, never as "0 fields", which is a claim. */}
                      <span className="text-xs text-fieldiq-muted">
                        {Number.isFinite(t.fieldCount) ? `${t.fieldCount} fields` : '— fields'}
                      </span>
                    </div>

                    {/* Library controls — server enforces these roles, so a user
                        who cannot use them is not shown a control that 403s. */}
                    {canManage && (
                      <div className="flex items-center gap-5 sm:shrink-0">
                        {draft ? (
                          <span className="text-xs text-fieldiq-muted italic">
                            Publish to add to the library
                          </span>
                        ) : (
                          <>
                            <label
                              className={`flex items-center gap-2 ${
                                empty && !enabled ? 'cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            >
                              <Switch
                                checked={enabled}
                                // Only the ON direction is blocked. A zero-field
                                // form that is ALREADY enabled has to stay
                                // switchable or the fix is locked away with the
                                // mistake.
                                disabled={empty && !enabled}
                                title={empty && !enabled ? NO_FIELDS_REASON : undefined}
                                onCheckedChange={(next) => handleToggleEnabled(t, next)}
                              />
                              <span className="text-xs text-fieldiq-muted">Enabled</span>
                            </label>

                            <label
                              className={`flex items-center gap-2 ${
                                enabled && !empty
                                  ? 'cursor-pointer'
                                  : 'cursor-not-allowed opacity-50'
                              }`}
                              title={
                                empty
                                  ? NO_FIELDS_REASON
                                  : enabled
                                    ? 'Make this the default form for this category'
                                    : 'Enable this form before making it the default'
                              }
                            >
                              <input
                                type="radio"
                                name={`default-${group.category}`}
                                checked={isDefault}
                                // Blocked here, before any request: the API
                                // rejects a default that is not in the enabled
                                // set, and a 422 is a worse way to learn it.
                                //
                                // A zero-field form is blocked for a different
                                // reason — the API would accept it. The default
                                // is what a crew is handed by default, so an
                                // empty form must never hold that slot.
                                disabled={!enabled || empty}
                                onChange={() => handleSetDefault(t)}
                                className="accent-fieldiq-accent w-4 h-4"
                              />
                              <span className="text-xs text-fieldiq-muted">Default</span>
                            </label>
                          </>
                        )}
                      </div>
                    )}

                    {/* Row actions */}
                    <div className="flex items-center gap-1 sm:shrink-0">
                      {/* Preview — the form as a crew will meet it.
                          Only where there is something to look at: a
                          zero-field template renders a page with nothing on
                          it but a Submit button, which teaches the reviewer
                          nothing and is exactly the state `empty` already
                          names on this row.
                          A NEW TAB, deliberately: the library screen holds
                          unsaved enable/default edits, and navigating away
                          from a dirty save bar to look at a form would throw
                          them away.
                          IT IS THE LIVE RENDERER, in its preview mode:
                          `?preview=1` keeps every rule live but disables Save
                          Draft and Submit and shows "Preview — not saved", so
                          looking at a form can no longer file a submission. */}
                      {!empty && (
                        <Tooltip content="Open this form the way a crew sees it, in a new tab">
                          <a
                            href={`/forms/dynamic/${t.id}?preview=1`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded-lg text-fieldiq-muted hover:bg-fieldiq-accent/20 hover:text-fieldiq-accent transition-colors"
                            aria-label={`Preview ${t.name}`}
                          >
                            <Eye size={14} />
                          </a>
                        </Tooltip>
                      )}

                      {draft && canPublish && (
                        <button
                          onClick={() => publishMutation.mutate(t.id)}
                          disabled={publishMutation.isPending}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 text-yellow-300 rounded-lg text-xs font-medium hover:bg-yellow-500/30 transition-colors disabled:opacity-50"
                        >
                          <Send size={14} />
                          Publish
                        </button>
                      )}

                      {editable ? (
                        <>
                          <Tooltip content="Edit">
                            <button
                              onClick={() => navigate(`/settings/safety/forms/${t.id}/edit`)}
                              className="p-1.5 rounded-lg text-fieldiq-muted hover:bg-fieldiq-accent/20 hover:text-fieldiq-accent transition-colors"
                              aria-label={`Edit ${t.name}`}
                            >
                              <Pencil size={14} />
                            </button>
                          </Tooltip>
                          <Tooltip content="Delete">
                            <button
                              onClick={() => setDeleteTarget(t)}
                              className="p-1.5 rounded-lg text-fieldiq-muted hover:bg-red-500/20 hover:text-red-400 transition-colors"
                              aria-label={`Delete ${t.name}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip content="Copy this platform form into your own library so you can edit its fields">
                          <button
                            onClick={() => cloneMutation.mutate(t.id)}
                            disabled={cloneMutation.isPending}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-fieldiq-border text-white rounded-lg text-xs font-medium hover:bg-fieldiq-accent/20 hover:text-fieldiq-accent transition-colors disabled:opacity-50"
                          >
                            <Copy size={14} />
                            Fork &amp; Edit
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* Save bar — one PUT per dirty category, fired on Save, never per toggle */}
      {/* Offset by --bottom-chrome below md so that, if it sticks, it sticks
          above the fixed bottom nav rather than under it (md and up have no
          bar). Today it does not stick at all: SettingsLayout's panel is
          overflow-hidden, which makes the panel its sticky ancestor, and the
          panel never scrolls. So the bar sits at the end of the list, clear
          of the nav thanks to the page's bottom padding. Measured at 390px. */}
      {canManage && dirty && (
        <div className="sticky bottom-[var(--bottom-chrome)] md:bottom-0 border-t border-fieldiq-border bg-fieldiq-card/95 backdrop-blur px-6 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-fieldiq-text">
              Unsaved changes in {dirtyCategories.length}{' '}
              {dirtyCategories.length === 1 ? 'category' : 'categories'}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEnabledEdits({});
                  setDefaultEdits({});
                }}
                disabled={saveMutation.isPending}
                className="px-4 py-2 bg-fieldiq-border text-white rounded-lg text-sm font-medium hover:bg-fieldiq-border/80 disabled:opacity-50"
              >
                Discard
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-fieldiq-accent text-white rounded-lg text-sm font-semibold hover:bg-fieldiq-accent/90 disabled:opacity-50"
              >
                {saveMutation.isPending && <Loader2 size={16} className="animate-spin" />}
                {saveMutation.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
          </DialogHeader>
          <p className="text-fieldiq-text text-sm">
            Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
          </p>
          <DialogFooter>
            <button
              onClick={() => setDeleteTarget(null)}
              className="px-4 py-2 bg-fieldiq-border text-white rounded-lg text-sm font-medium hover:bg-fieldiq-border/80"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
              className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
